import type {Partition} from "@common-core/web-component-runtime/runtime";

/**
 * The `partition` query parameter the Bridge Bar API expects, derived from the runtime's own
 * partition name.
 *
 * Shared by every call the selector makes rather than derived at each call site: the search
 * endpoint authorizes itself from the membership endpoint's decision, and that decision is
 * partition-scoped, so two callers disagreeing about the spelling would let a user be in search
 * mode on one request and refused on the next.
 *
 * An exact match on a closed union, not a substring test. `Runtime.partition` is
 * `"prod" | "non-prod"` and nothing else: an adopter's arbitrary `partition` attribute cannot
 * widen it, because `normalizePartition` trims, lowercases, and clamps anything unrecognised to
 * `"prod"` before a runtime is ever selected (see `normalizeBridgeBarConfig`, which owns that
 * guarantee and tests it). So there is no third spelling to be lenient about, and being lenient
 * anyway would only add a way to misclassify a value that cannot occur — `"prod-non-prod-mirror"`
 * reading as non-prod, say. The backend's own parser takes the same line on purpose:
 * `RequestPartition` says "Deliberately no substring matching".
 */
export function toPartitionQueryParameter(
  partition: Partition
): "PROD" | "NON_PROD" {
  return partition === "non-prod" ? "NON_PROD" : "PROD";
}
